class EngravingCartDrawerItemActions extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.engravingData = this.getEngravingData();
    this.currentProductEngravingOptions = [];
    this.ringEngravingOptions = [];
    this.watchEngravingOptions = [];
    this.pendantEngravingOptions = [];
    this.engravingProperties = [
      "_Line1",
      "_Line2",
      "_Line3",
      "_engraving_service_price",
      "_engraving_service_variant_id",
      "_engraving_type",
    ];
    this.productProperties = this.getProductProperties();
  }

  connectedCallback() {
    this.loadEngravingBasedOnProductType();
    this.loadProductEngravingOptions();
    this.initElements();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    this.addEngraving?.removeEventListener(
      "click",
      this.handleAddEngravingClick.bind(this)
    );
    this.editEngraving?.removeEventListener(
      "click",
      this.handleEditEngravingClick.bind(this)
    );
    this.removeEngraving?.removeEventListener(
      "click",
      this.handleRemoveEngravingClick.bind(this)
    );
  }

  /** Initializes DOM elements and updates dataset properties. */
  initElements() {
    this.addEngraving = this.querySelector(
      ".cart-drawer-item--engraving-action-add"
    );

    this.editEngraving = this.querySelector(
      ".cart-drawer-item--engraving-action-edit"
    );

    this.removeEngraving = this.querySelector(
      ".cart-drawer-item--engraving-action-remove"
    );

    this.engravingLines = this.querySelector("ul");

    if (
      this.dataset.engravingType === "free_engraving" ||
      this.currentProductEngravingOptions.length > 0
    ) {
      this.dataset.showEngravingActions = true;
    } else {
      this.dataset.showEngravingActions = false;
    }
  }

  /** Attaches event listeners for engraving actions. */
  attachEventListeners() {
    this.addEngraving?.addEventListener(
      "click",
      this.handleAddEngravingClick.bind(this)
    );
    this.editEngraving?.addEventListener(
      "click",
      this.handleEditEngravingClick.bind(this)
    );
    this.removeEngraving?.addEventListener(
      "click",
      this.handleRemoveEngravingClick.bind(this)
    );
  }

  /** Loads engraving options based on product type. */
  loadEngravingBasedOnProductType() {
    const { itemType } = this.dataset;

    if (itemType.includes("watch")) {
      this.watchEngravingOptions = this.engravingData.filter((el) =>
        ["caseback", "top-arch-caseback", "sidewall"].includes(el.engravingType)
      );
      return;
    }

    if (itemType.includes("ring")) {
      this.ringEngravingOptions = this.engravingData.filter(
        (el) => el.engravingType === "ring"
      );
      return;
    }

    if (itemType.includes("pendant") || itemType.includes("necklace")) {
      this.pendantEngravingOptions = this.engravingData.filter(
        (el) => el.engravingType === "pendant"
      );
      return;
    }
  }

  /** Loads engraving options specific to the current product. */
  loadProductEngravingOptions() {
    const { itemType, itemHandle } = this.dataset;
    if (itemType.includes("watch")) {
      this.currentProductEngravingOptions = this.watchEngravingOptions.filter(
        (el) => el.associatedProducts.includes(itemHandle)
      );
      return;
    }
    if (itemType.includes("ring")) {
      this.currentProductEngravingOptions = this.ringEngravingOptions.filter(
        (el) => el.associatedProducts.includes(itemHandle)
      );
      return;
    }
    if (itemType.includes("pendant") || itemType.includes("necklace")) {
      this.currentProductEngravingOptions = this.pendantEngravingOptions.filter(
        (el) => el.associatedProducts.includes(itemHandle)
      );
      return;
    }
  }

  /**
   * Handles the add engraving button click event.
   * @param {Event} event - The click event.
   */
  handleAddEngravingClick(event) {
    const btn = event.target.closest(".cart-drawer-item--engraving-action-add");
    if (!btn) return;
    const addEngravingEvent = new CustomEvent("cartItemAddEngraving", {
      detail: {
        currentProductEngravingOptions: this.currentProductEngravingOptions,
        productVariantId: this.dataset.itemId,
        productProperties: this.getCleanProductProperties(),
      },
    });
    document.dispatchEvent(addEngravingEvent);
    this.cart.close();
  }

  /**
   * Handles the edit engraving button click event.
   * @param {Event} event - The click event.
   */

  handleEditEngravingClick(event) {
    const btn = event.target.closest(
      ".cart-drawer-item--engraving-action-edit"
    );
    if (!btn) return;
    const engravingData = this.getItemEngravingLines();
    const editEngravingEvent = new CustomEvent("cartItemEditEngraving", {
      detail: {
        engravingData,
        productVariantId: this.dataset.itemId,
        currentProductEngravingOptions: this.currentProductEngravingOptions,
        productProperties: this.getCleanProductProperties(),
      },
    });
    document.dispatchEvent(editEngravingEvent);
    this.cart.close();
  }

  /**
   * Handles the remove engraving button click event.
   * @param {Event} event - The click event.
   */
  handleRemoveEngravingClick(event) {
    const btn = event.target.closest(
      ".cart-drawer-item--engraving-action-remove"
    );
    if (!btn) return;
    this.removeEngravingProperties();
  }

  async removeEngravingProperties() {
    let newProperties = this.getCleanProductProperties();
    let cartData = {
      id: this.dataset.itemId,
      properties: {
        ...newProperties,
      },
    };
    try {
      const request = await fetch(
        window.Shopify.routes.root + "cart/change.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cartData),
        }
      );
      const response = await request.json();
      this.renderCartDrawer(response);
    } catch (error) {
      console.log(``);
    }
  }

  getCleanProductProperties() {
    let newProperties = { ...this.productProperties };
    this.engravingProperties.forEach((item) => {
      delete newProperties[item];
    });

    return !Object.keys(newProperties).length
      ? { _engraving_removed: true }
      : newProperties;
  }

  getItemEngravingLines() {
    return {
      engravingType: this.engravingLines.dataset.engravingType,
      engravingLines: Array.from(
        this.engravingLines.querySelectorAll("li")
      ).map((el) => el.dataset.engravingLine),
    };
  }

  /**
   * Retrieves engraving data from the DOM.
   * @returns {Object} Parsed engraving data.
   */
  getEngravingData() {
    return JSON.parse(
      document.querySelector("#engraving-options-data").textContent
    ).engravingOptions;
  }

  /** Renders the cart drawer with the updated cart response.
   * @param {Object} response - The cart response object. */
  renderCartDrawer(response) {
    const { sections } = response;

    const parsedResponse = {
      id: response.id,
      sections,
    };

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "engraving-form",
      productVariantId: parsedResponse.id,
      cartData: parsedResponse,
    });

    this.cart.renderContents(parsedResponse);

    if (this.cart && this.cart.classList.contains("is-empty"))
      this.cart.classList.remove("is-empty");
  }

  getProductProperties() {
    return JSON.parse(
      this.querySelector(`script[type="application/json"]`).textContent
    );
  }
}
customElements.define(
  "cart-drawer-item-engraving-actions",
  EngravingCartDrawerItemActions
);
